import { useCallback } from 'react';
import { useAppStore, useToastState } from '../store/useAppStore';

export const useFileUpload = () => {
  const {
    addPdfFile,
    removePdfFile: removePdfAttachment,
    setAudioFile,
    setImageFile,
    pdfFiles,
    audioFile,
    imageFile
  } = useAppStore();
  const { showToast } = useToastState();

  // Validação de arquivo PDF
  const validatePdfFile = useCallback((file: File): { isValid: boolean; error?: string } => {
    if (!file) {
      return { isValid: false, error: 'Nenhum arquivo selecionado' };
    }

    if (file.type !== 'application/pdf') {
      return { isValid: false, error: 'Apenas arquivos PDF são permitidos' };
    }

    // Verificar tamanho (50MB máximo)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      return { isValid: false, error: 'Arquivo muito grande. Máximo: 50MB' };
    }

    return { isValid: true };
  }, []);

  // Validação de arquivo de áudio
  const validateAudioFile = useCallback((file: File): { isValid: boolean; error?: string } => {
    if (!file) {
      return { isValid: false, error: 'Nenhum arquivo selecionado' };
    }

    if (file.type !== 'audio/ogg' || !file.name.toLowerCase().endsWith('.ogg')) {
      return { isValid: false, error: 'Apenas arquivos .ogg (opus) são permitidos' };
    }

    // Verificar tamanho (50MB máximo)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      return { isValid: false, error: 'Arquivo muito grande. Máximo: 50MB' };
    }

    return { isValid: true };
  }, []);

  // Validacao de arquivo de imagem
  const validateImageFile = useCallback((file: File): { isValid: boolean; error?: string } => {
    if (!file) {
      return { isValid: false, error: 'Nenhum arquivo selecionado' };
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.webp'];

    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'Apenas arquivos PNG, JPG, JPEG ou WebP sao permitidos' };
    }

    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      return { isValid: false, error: 'Extensao de arquivo invalida' };
    }

    // Verificar tamanho (50MB maximo)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      return { isValid: false, error: 'Arquivo muito grande. Maximo: 50MB' };
    }

    return { isValid: true };
  }, []);

  // Handler para upload de PDF: aceita vários de uma vez e cada um vira um
  // anexo próprio (antes só o último ficava, e a sequência mandava ele N vezes).
  const handlePdfUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = ''; // Limpar input: permite escolher o mesmo arquivo de novo

    if (selected.length === 0) {
      return;
    }

    const accepted: string[] = [];
    for (const file of selected) {
      const validation = validatePdfFile(file);
      if (!validation.isValid) {
        showToast({
          type: 'error',
          title: 'Arquivo Inválido',
          message: `${file.name}: ${validation.error}`
        });
        continue;
      }
      addPdfFile(file);
      accepted.push(file.name);
      console.log('📄 PDF selecionado:', { name: file.name, size: file.size });
    }

    if (accepted.length > 0) {
      showToast({
        type: 'success',
        title: accepted.length === 1 ? 'PDF Selecionado' : `${accepted.length} PDFs Selecionados`,
        message: `${accepted.join(', ')} carregado${accepted.length === 1 ? '' : 's'} com sucesso.`
      });
    }
  }, [validatePdfFile, addPdfFile, showToast]);

  // Handler para upload de áudio
  const handleAudioUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (!file) {
      return;
    }

    const validation = validateAudioFile(file);
    
    if (!validation.isValid) {
      showToast({
        type: 'error',
        title: 'Arquivo Inválido',
        message: validation.error
      });
      event.target.value = ''; // Limpar input
      return;
    }

    setAudioFile(file);
    showToast({
      type: 'success',
      title: 'Áudio Selecionado',
      message: `Arquivo "${file.name}" carregado com sucesso.`
    });
    console.log('🎵 Áudio selecionado:', { name: file.name, size: file.size });
  }, [validateAudioFile, setAudioFile, showToast]);

  // Handler para upload de imagem
  const handleImageUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const validation = validateImageFile(file);

    if (!validation.isValid) {
      showToast({
        type: 'error',
        title: 'Arquivo Invalido',
        message: validation.error
      });
      event.target.value = ''; // Limpar input
      return;
    }

    setImageFile(file);
    showToast({
      type: 'success',
      title: 'Imagem Selecionada',
      message: `Arquivo "${file.name}" carregado com sucesso.`
    });
    console.log('🖼️ Imagem selecionada:', { name: file.name, size: file.size });
  }, [validateImageFile, setImageFile, showToast]);

  // Remover um PDF específico (e os itens da sequência que apontam para ele)
  const removePdfFile = useCallback((id: string) => {
    removePdfAttachment(id);
    console.log('📄 PDF removido', { id });
  }, [removePdfAttachment]);

  // Remover arquivo de áudio
  const removeAudioFile = useCallback(() => {
    setAudioFile(null);
    console.log('🎵 Áudio removido');
  }, [setAudioFile]);

  // Remover arquivo de imagem
  const removeImageFile = useCallback(() => {
    setImageFile(null);
    console.log('🖼️ Imagem removida');
  }, [setImageFile]);

  // Limpar todos os arquivos
  const clearAllFiles = useCallback(() => {
    pdfFiles.forEach(p => removePdfAttachment(p.id));
    setAudioFile(null);
    setImageFile(null);
    console.log('🧹 Todos os arquivos removidos');
  }, [pdfFiles, removePdfAttachment, setAudioFile, setImageFile]);

  // Formatar tamanho do arquivo
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // Verificar se tem arquivos selecionados
  const hasFiles = !!(pdfFiles.length || audioFile || imageFile);

  // Informações dos arquivos
  const fileInfo = {
    pdfs: pdfFiles.map(p => ({
      id: p.id,
      name: p.file.name,
      size: formatFileSize(p.file.size),
      type: p.file.type
    })),
    audio: audioFile ? {
      name: audioFile.name,
      size: formatFileSize(audioFile.size),
      type: audioFile.type
    } : null,
    image: imageFile ? {
      name: imageFile.name,
      size: formatFileSize(imageFile.size),
      type: imageFile.type
    } : null
  };

  return {
    // Estado
    pdfFiles,
    audioFile,
    imageFile,
    hasFiles,
    fileInfo,

    // Handlers
    handlePdfUpload,
    handleAudioUpload,
    handleImageUpload,
    removePdfFile,
    removeAudioFile,
    removeImageFile,
    clearAllFiles,

    // Validadores
    validatePdfFile,
    validateAudioFile,
    validateImageFile,

    // Utils
    formatFileSize
  };
};